/**
 * Pure helpers for itinerary-driven realtime vehicle auto-selection.
 *
 * Resolution chain used to bind an itinerary transit leg to a realtime trip:
 * `leg.providerId` is the MOTIS `tripId` formatted as `{YYYYMMDD}_{H:MM}_{dataset_tag}_{gtfs_trip_id}`;
 * stripping the leading `date_time_` segment yields the realtime store `trip_id` verbatim
 * (MOTIS dataset tags match the GTFS source prefixes used by the realtime service).
 */

import type { ItineraryDTO, ItineraryLegDTO, ItineraryLegStepDTO } from "../types/dtos/api/itinerary/itinerary.dto";
import type { StopAnchorWire, TripDelayWire, TripIndexEntry } from "../types/dtos/realtime-api/realtime.dto";
import { LegType } from "../types/itinerary/leg-type.enum";
import { normalizeGtfsParentStationId } from "./gtfsStopId.tools";
import { isTransitLikeTransportMode } from "./TransportModeGroupsTools";

/** MOTIS `tripId` decoration: `{YYYYMMDD}_{H:MM}_` prepended to the GTFS trip id. */
const MOTIS_TRIP_DATE_PREFIX_PATTERN = /^\d{8}_\d{1,2}:\d{2}_/u;

/**
 * Removes the leading `YYYYMMDD_H:MM_` segment MOTIS adds to GTFS trip ids.
 *
 * @param providerId MOTIS-decorated or raw trip id.
 * @returns Trip id without the departure-date decoration.
 */
export const stripMotisTripDatePrefix = ( providerId: string ): string => providerId.trim().replace( MOTIS_TRIP_DATE_PREFIX_PATTERN, "" );

/**
 * Resolves a leg `providerId` to a realtime store `trip_id` via the trip index.
 *
 * @param providerId Leg provider id (MOTIS `tripId`).
 * @param tripIndex Realtime trip index keyed by static trip id.
 * @returns Matching realtime `trip_id`, or `null` when absent from the store.
 */
export const resolveRealtimeTripIdFromProviderId = (
    providerId: string,
    tripIndex: ReadonlyMap<string, TripIndexEntry>,
): string | null => {
    const trimmed = providerId.trim();
    if ( trimmed === "" ) {
        return null;
    }

    if ( tripIndex.has( trimmed ) ) {
        return trimmed;
    }

    const stripped = stripMotisTripDatePrefix( trimmed );

    return tripIndex.has( stripped ) ? stripped : null;
};

/**
 * Finds the transit leg currently in progress on an itinerary.
 *
 * The window uses the leg endpoint `time` values, which already carry realtime
 * corrections when the backend enrich pass ran (`scheduledTime` keeps the original).
 *
 * @param itinerary Itinerary displayed on the map.
 * @param nowMs Current timestamp in milliseconds.
 * @returns The in-progress transit leg, or `null` when none matches.
 */
export const findActiveTransitLeg = (
    itinerary: ItineraryDTO,
    nowMs: number,
): ItineraryLegDTO | null => {
    for ( const leg of itinerary.legs ) {
        if ( leg.type !== LegType.publicTransport ) {
            continue;
        }

        const providerId = leg.providerId?.trim() ?? "";
        if ( providerId === "" || !isTransitLikeTransportMode( leg.mode ) ) {
            continue;
        }

        const fromMs = Date.parse( leg.from?.time ?? "" );
        const toMs = Date.parse( leg.to?.time ?? "" );
        if ( !Number.isFinite( fromMs ) || !Number.isFinite( toMs ) ) {
            continue;
        }

        if ( fromMs <= nowMs && nowMs <= toMs ) {
            return leg;
        }
    }

    return null;
};

/**
 * Normalizes a Swiss or French UIC station code embedded in a GTFS stop id.
 *
 * Eight-digit codes that start with 85 or 87 drop the trailing check digit so a
 * parent `StopArea` and a child `StopPoint` of the same station compare equal.
 * Mirrors `normalizeStationUic` in backend `itinerary-realtime-enrich.service.ts`.
 *
 * @param stopId GTFS or MOTIS stop id.
 * @returns Seven-digit UIC when one is present, otherwise `null`.
 */
export const normalizeStationUic = ( stopId: string ): string | null => {
    const matches = stopId.match( /\d{7,8}/gu );
    if ( matches === null ) {
        return null;
    }

    for ( const raw of matches ) {
        const digits = raw.length === 8 && ( raw.startsWith( "85" ) || raw.startsWith( "87" ) )
            ? raw.slice( 0, 7 )
            : raw;
        if ( digits.startsWith( "85" ) || digits.startsWith( "87" ) ) {
            return digits;
        }
    }

    return null;
};

/**
 * Normalizes a MOTIS parent stop id (`{tag}_Parent{gtfs_id}`) toward a child stop id.
 *
 * @param parentStopId Raw `stop.parent` value.
 * @returns Normalized parent candidate, or `null` when absent/empty.
 */
const normalizeParentStopId = ( parentStopId: string | undefined ): string | null => {
    if ( parentStopId === undefined ) {
        return null;
    }

    const normalized = normalizeGtfsParentStationId( parentStopId ).replace( "_Parent", "_" );

    return normalized === "" ? null : normalized;
};

/**
 * Resolves the GTFS `stop_sequence` of an itinerary step stop against a trip's stop map.
 *
 * Mirrors backend `resolveStopSequence`: exact stop id, normalized parent, then
 * `:`-separated prefix containment in both directions, and finally UIC station equality.
 *
 * @param stopId Step `stop.id` (MOTIS `stopId`).
 * @param parentStopId Optional step `stop.parent` (MOTIS `parentId`).
 * @param stopIdToSeq Map of the trip's `stop_id` → `stop_sequence`.
 * @returns Matching `stop_sequence`, or `undefined` when unknown.
 */
export const resolveStopSequence = (
    stopId: string,
    parentStopId: string | undefined,
    stopIdToSeq: ReadonlyMap<string, number>,
): number | undefined => {
    const exact = stopIdToSeq.get( stopId );
    if ( exact !== undefined ) {
        return exact;
    }

    const normalizedParent = normalizeParentStopId( parentStopId );
    if ( normalizedParent !== null ) {
        const parentExact = stopIdToSeq.get( normalizedParent );
        if ( parentExact !== undefined ) {
            return parentExact;
        }
    }

    const candidates = normalizedParent === null ? [ stopId ] : [ stopId, normalizedParent ];
    for ( const candidate of candidates ) {
        for ( const [ mappedStopId, seq ] of stopIdToSeq ) {
            if ( mappedStopId.startsWith( `${ candidate }:` ) || candidate.startsWith( `${ mappedStopId }:` ) ) {
                return seq;
            }
        }
    }

    for ( const candidate of candidates ) {
        const candidateUic = normalizeStationUic( candidate );
        if ( candidateUic === null ) {
            continue;
        }

        for ( const [ mappedStopId, seq ] of stopIdToSeq ) {
            if ( normalizeStationUic( mappedStopId ) === candidateUic ) {
                return seq;
            }
        }
    }

    return undefined;
};

/**
 * Computes the corrected delay of one step from `time` vs `scheduledTime`.
 *
 * @param step Itinerary leg step.
 * @returns Delay in seconds, or `null` when the step carries no realtime correction.
 */
const stepDelaySeconds = ( step: ItineraryLegStepDTO ): number | null => {
    if ( step.scheduledTime === undefined || step.scheduledTime === "" ) {
        return null;
    }

    const correctedMs = Date.parse( step.time );
    const scheduledMs = Date.parse( step.scheduledTime );
    if ( !Number.isFinite( correctedMs ) || !Number.isFinite( scheduledMs ) ) {
        return null;
    }

    return Math.round( ( correctedMs - scheduledMs ) / 1000 );
};

/**
 * Builds a realtime delay payload from the corrected times of an enriched itinerary leg.
 *
 * The backend enrich pass (GTFS-RT, SIRI, search.ch stationboard) writes `scheduledTime`
 * on corrected steps; the diff against `time` becomes a per-stop delay mapped onto the
 * trip's `stop_sequence` values. `global` takes the delay at the first corrected step,
 * i.e. the boarding point of the leg.
 *
 * @param leg Transit leg carrying realtime corrections.
 * @param resolvedTripId Realtime store `trip_id` the wire applies to.
 * @param scheduleStops Ordered stop anchors of the realtime trip schedule.
 * @returns Delay wire for interpolation override, or `null` when the leg has no corrections.
 */
export const buildTripDelayWireFromLeg = (
    leg: ItineraryLegDTO,
    resolvedTripId: string,
    scheduleStops: readonly StopAnchorWire[],
): TripDelayWire | null => {
    const stopIdToSeq = new Map<string, number>();
    for ( const stop of scheduleStops ) {
        stopIdToSeq.set( stop.stop_id, stop.seq );
    }

    const steps: ItineraryLegStepDTO[] = [];
    if ( leg.from !== undefined ) {
        steps.push( leg.from );
    }
    for ( const intermediate of leg.intermediateStops ?? [] ) {
        steps.push( intermediate );
    }
    if ( leg.to !== undefined ) {
        steps.push( leg.to );
    }

    const stopDelays: [number, number][] = [];
    let globalDelay: number | null = null;

    for ( const step of steps ) {
        const delaySeconds = stepDelaySeconds( step );
        if ( delaySeconds === null ) {
            continue;
        }

        if ( globalDelay === null ) {
            globalDelay = delaySeconds;
        }

        const stopId = step.stop?.id?.trim() ?? "";
        if ( stopId === "" ) {
            continue;
        }

        const seq = resolveStopSequence( stopId, step.stop?.parent, stopIdToSeq );
        if ( seq !== undefined ) {
            stopDelays.push( [ seq, delaySeconds ] );
        }
    }

    if ( globalDelay === null ) {
        return null;
    }

    return {
        "trip_id": resolvedTripId,
        "global":  globalDelay,
        "stops":   stopDelays,
    };
};
